import type { ReactNode } from 'react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { LoadFailed } from '../ui/LoadState';
import { Skeleton } from '../../ui/Skeleton';
import { btnPrimary, btnSecondary } from '../ui/styles';
import { useCan } from '../useCan';
import { useCostsDraft } from '../margin/useCosts';
import { MarginLines } from '../margin/MarginLines';
import { boxContribution, minBoxFor, orderContribution } from '../../core/rules/margin';
import { euro, money } from '../../core/format/currency';
import { dayShort, today } from '../../core/format/dates';
import { exVat } from '../../core/rules/revenue';
import type { CostModel, FeeKey, ShipKind } from '../../core/types/margin';
const FEES: {
  key: FeeKey;
  label: string;
  hint: string;
}[] = [{
  key: 'ideal',
  label: 'iDEAL / Wero',
  hint: 'Mollie, per betaling'
}, {
  key: 'bancontact',
  label: 'Bancontact',
  hint: 'Mollie, België'
}, {
  key: 'card',
  label: 'Creditcard',
  hint: 'Mollie'
}, {
  key: 'klarna',
  label: 'Klarna',
  hint: 'Achteraf betalen'
}, {
  key: 'incasso',
  label: 'Incasso (bijvuldoos)',
  hint: 'Ook bij een mislukte incasso'
}, {
  key: 'pin',
  label: 'Pinnen in de salon',
  hint: 'Pinautomaat; contant kost niets'
}];
const SHIPS: {
  key: ShipKind;
  label: string;
}[] = [{
  key: 'brievenbus-nl',
  label: 'Brievenbuspakje NL'
}, {
  key: 'pakket-nl',
  label: 'Pakket NL'
}, {
  key: 'pakket-be',
  label: 'Pakket België'
}];

/** Een gemiddeld voorbeeld: serum van € 56 met 40% brutomarge. */
const EXAMPLE_GROSS = 5600;
const EXAMPLE_MARGIN = 40;
const cents = (euros: number | undefined) => Math.round((euros ?? 0) * 100);

/**
 * Instellingen › Kosten en marge. Alles zonder btw en ingeschat tot de echte Mollie- en
 * PostNL-cijfers er zijn. Sarah's eigen tijd telt bewust niet mee. Opslaan gebeurt hier zelf
 * (eigen knop), niet via de Opslaan-balk: het staat los van de site-instellingen en is nooit publiek.
 */
export function CostSettings() {
  const {
    canAll
  } = useCan();
  // Instellingen toont dit onderwerp alleen met finance.margin; hier nog eens, zodat het nooit 403 vraagt.
  return canAll('finance.margin') ? <CostSettingsForm canEdit={canAll('settings.edit')} data-id="element-4113" data-mp="3cneuyt87iymd" /> : null;
}
function CostSettingsForm({
  canEdit
}: {
  canEdit: boolean;
}) {
  const draft = useCostsDraft();
  const c = draft.current;
  if (draft.status === 'error' && !c) return <LoadFailed title="De kosten konden niet laden" onRetry={draft.reload} data-id="element-4114" data-mp="2orsnf2bxi2qt" />;
  if (!c) return <Skeleton className="h-64 w-full rounded-lg" data-id="element-4115" data-mp="2tqnfyvzp75o4" />;
  const edit = (fn: (m: CostModel) => CostModel) => draft.edit(fn);
  const exampleNet = exVat(EXAMPLE_GROSS, 21);
  const example = orderContribution({
    kind: 'los',
    lines: [{
      name: 'Serum',
      qty: 1,
      grossCents: EXAMPLE_GROSS,
      vatRate: 21,
      costCents: Math.round(exampleNet * (1 - EXAMPLE_MARGIN / 100))
    }],
    paymentMethod: 'ideal',
    paidCents: EXAMPLE_GROSS,
    shipping: 'pakket-nl'
  }, c);
  const advice = minBoxFor(c.targetBoxLeftCents, EXAMPLE_MARGIN, c);
  return <>
      <Panel title="Zo rekent het nu" description="Een losse bestelling van € 56 met gratis verzending, bij 40% brutomarge. Rekent mee terwijl je typt." data-id="element-4116" data-mp="23ftncrcy8ayb">
        <MarginLines breakdown={example} className="max-w-md" data-id="element-4117" data-mp="1tq1t5y8rhjud" />
        <p className="mt-4 text-[12px] leading-relaxed text-muted" data-id="element-4118" data-mp="1toh06d23i8ym">
          “Wat er overblijft” is de bijdrage aan je vaste lasten en aan jou, geen nettowinst. Huur, software en je eigen tijd tellen niet mee. Alle bedragen zonder btw, ingeschat · laatst
          nagelopen {dayShort(c.checkedOn)}.
        </p>
      </Panel>

      <fieldset disabled={!canEdit || draft.saving} className="space-y-6 disabled:opacity-90" data-id="element-4119" data-mp="1arfqwg1idmov">
        <legend className="sr-only" data-id="element-4120" data-mp="15te7fu067lcv">Kosten</legend>
        {!canEdit && <p className="text-[13px] text-muted" data-id="element-4121" data-mp="15uyzncz0is7k">Je kunt de kosten bekijken; aanpassen kan alleen wie de instellingen beheert.</p>}

        <Panel title="Betalen" description="Vast bedrag plus een percentage van het bedrag, per betaling." data-id="element-4122" data-mp="3075h2tii258b">
          <ul className="divide-y divide-line" data-id="element-4123" data-mp="1xsy01bz2blgw">
            {FEES.map(f => <li key={f.key} className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_8rem_7rem] sm:items-center sm:gap-4" data-id="element-4124" data-mp="21ut3am0k08xb">
                <span className="min-w-0" data-id="element-4125" data-mp="1nuib9fnicoak">
                  <span className="block text-[14px] text-ink" data-id="element-4126" data-mp="1suegfi7xylrp">{f.label}</span>
                  <span className="block text-[12px] text-muted" data-id="element-4127" data-mp="1swhgkv2t5va5">{f.hint}</span>
                </span>
                <Labeled label={`${f.label}: vast bedrag in euro`} suffix="€" data-id="element-4128" data-mp="1e3dy8lhe7did">
                  <NumberInput decimals value={c.payment[f.key].fixedCents / 100} onChange={v => edit(m => ({
                ...m,
                payment: {
                  ...m.payment,
                  [f.key]: {
                    ...m.payment[f.key],
                    fixedCents: cents(v)
                  }
                }
              }))} max={5} data-id="element-4129" data-mp="1ixrv6sbpdsp5" />
                </Labeled>
                <Labeled label={`${f.label}: percentage`} suffix="%" data-id="element-4130" data-mp="v6mkerkmdqlp">
                  <NumberInput decimals value={c.payment[f.key].percent} onChange={v => edit(m => ({
                ...m,
                payment: {
                  ...m.payment,
                  [f.key]: {
                    ...m.payment[f.key],
                    percent: v ?? 0
                  }
                }
              }))} max={20} data-id="element-4131" data-mp="v8phfvk6wjzx" />
                </Labeled>
              </li>)}
          </ul>
        </Panel>

        <Panel title="Verzenden en verpakking" description="Wat jij betaalt per zending, ook als de klant gratis verzending heeft." data-id="element-4132" data-mp="ck57nzq14j3m">
          <div className="grid gap-5 sm:grid-cols-3" data-id="element-4133" data-mp="lj0gljkgk7am">
            {SHIPS.map(s => <Field key={s.key} label={`${s.label} (€)`} data-id="element-4134" data-mp="lld226pfczsf">
                <NumberInput decimals value={c.shipping[s.key] / 100} onChange={v => edit(m => ({
              ...m,
              shipping: {
                ...m.shipping,
                [s.key]: cents(v)
              }
            }))} max={50} data-id="element-4135" data-mp="zhgsrq13aq05" />
              </Field>)}
            <Field label="Verpakking pakket (€)" hint="Doos, vulling, sticker en kaartje." data-id="element-4136" data-mp="198377gx2lah9">
              <NumberInput decimals value={c.packaging.pakket / 100} onChange={v => edit(m => ({
              ...m,
              packaging: {
                ...m.packaging,
                pakket: cents(v)
              }
            }))} max={20} data-id="element-4137" data-mp="14a1mkrjzx33o" />
            </Field>
            <Field label="Verpakking brievenbus (€)" data-id="element-4138" data-mp="1e8z77jeawche">
              <NumberInput decimals value={c.packaging.brievenbus / 100} onChange={v => edit(m => ({
              ...m,
              packaging: {
                ...m.packaging,
                brievenbus: cents(v)
              }
            }))} max={20} data-id="element-4139" data-mp="1law3mhbvtazh" />
            </Field>
          </div>
        </Panel>

        <Panel title="Retouren en bijvullen" data-id="element-4140" data-mp="1v8s7co2hp3nk">
          <div className="grid gap-5 sm:grid-cols-3" data-id="element-4141" data-mp="1v6z3fgj9gxj9">
            <Field label="Retourvoorziening (%)" hint="Van de productomzet in de webshop." data-id="element-4142" data-mp="200tu53xaacru">
              <NumberInput decimals value={c.returnsPercent} onChange={v => edit(m => ({
              ...m,
              returnsPercent: v ?? 0
            }))} max={50} data-id="element-4143" data-mp="24zmv5wvdwbb4" />
            </Field>
            <Field label="Per bijvuldoos minstens over (€)" hint="Voor het advies bij het minimumbedrag." data-id="element-4144" data-mp="292b74dfe9ycb">
              <NumberInput decimals value={c.targetBoxLeftCents / 100} onChange={v => edit(m => ({
              ...m,
              targetBoxLeftCents: cents(v)
            }))} max={200} data-id="element-4145" data-mp="2iymkz7umuw8v" />
            </Field>
            <Field label="Abonnementscadeau kost (€)" hint="€ 0 als het gratis van de leverancier komt (try-me sizes)." data-id="element-4146" data-mp="jnd2sj277z5y">
              <NumberInput decimals value={c.subscriptionGiftCents / 100} onChange={v => edit(m => ({
              ...m,
              subscriptionGiftCents: cents(v)
            }))} max={100} data-id="element-4147" data-mp="h140n42a399l" />
            </Field>
          </div>
          <p className="mt-4 text-[13px] text-muted" data-id="element-4148" data-mp="c8j8s82xn68p">
            {advice === null ? 'Bij 40% brutomarge dekt een bijvuldoos deze kosten niet.' : `Bij 40% brutomarge laat een doos van € 75 ${money(boxContribution(7500, EXAMPLE_MARGIN, c))} over; voor ${euro(c.targetBoxLeftCents / 100, {
            cents: c.targetBoxLeftCents % 100 !== 0
          })} per doos is het minimum ± ${euro(advice)}.`}
          </p>
        </Panel>
      </fieldset>

      {canEdit && <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-end gap-3 rounded-lg border border-line bg-white px-4 py-3 shadow-sm" data-id="element-4149" data-mp="pzxsyi8ktdhc">
          <span className="mr-auto text-[13px] text-muted" data-id="element-4150" data-mp="3asj6ilsybrzj">{draft.dirty ? 'Niet opgeslagen wijzigingen' : `Laatst nagelopen ${dayShort(c.checkedOn)}`}</span>
          {draft.dirty ? <button type="button" onClick={draft.discard} disabled={draft.saving} className={btnSecondary} data-id="element-4151" data-mp="3vhbyewce9x9o">
              Verwerpen
            </button> : <button type="button" onClick={() => edit(m => ({
        ...m,
        checkedOn: today()
      }))} className={btnSecondary} data-id="element-4152" data-mp="48ocae6by612">
              Alles klopt, vandaag nagelopen
            </button>}
          <button type="button" onClick={() => void draft.save()} disabled={!draft.dirty || draft.saving} className={btnPrimary} data-id="element-4153" data-mp="3qi91y0ahczdb">
            {draft.saving ? 'Opslaan…' : 'Kosten opslaan'}
          </button>
        </div>}
    </>;
}

/** Een veld zonder zichtbaar label (de rij zegt al wat het is), met eenheid erachter. */
function Labeled({
  label,
  suffix,
  children
}: {
  label: string;
  suffix: string;
  children: ReactNode;
}) {
  return <label className="flex items-center gap-2" data-id="element-4154" data-mp="110i5rmxr4wnq">
      <span className="sr-only" data-id="element-4155" data-mp="10zqn1cp96tev">{label}</span>
      <span className="min-w-0 flex-1" data-id="element-4156" data-mp="15s9zu7g49kim">{children}</span>
      <span aria-hidden="true" className="w-3 text-[13px] text-muted" data-id="element-4157" data-mp="15tuudx2fl3gh">
        {suffix}
      </span>
    </label>;
}